/**
 * Classifies and converts a listing's virtual_tour_url. Surveying every
 * real value on file (2026-10-13) turned up two clusters worth telling
 * apart - a "Video" (YouTube, Vimeo, and a handful of other video
 * delivery hosts) and an actual interactive 3D walkthrough ("Virtual
 * Tour" - Matterport, RealSee, Zillow 3D Home, etc.). Everything else
 * (photography-studio microsites, file hosts, the occasional garbage
 * value like a literal "Yes") is unknown and keeps the original,
 * pre-this-feature "Virtual Tour" label rather than guessing either way.
 *
 * Shared by PropertyModal (label + inline embed) and ListingsDrawer
 * (badge label) so the two surfaces can't drift out of agreement about
 * what a given URL actually is.
 */
// "Video" label. Of these, only youtube/vimeo have a known embeddable
// form (see toEmbeddableTourUrl) - the rest still open in a new tab, just
// correctly labeled as a video rather than a tour.
const VIDEO_HOSTS = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'videodelivery.net',
    'bcove.video',
    'wixstatic.com',
    'brightcove.net',
    'wistia.com',
    'safeshare.tv',
];
// "Virtual Tour" label. Matterport's own share link already works as an
// iframe src as-is (see toEmbeddableTourUrl); the others have no known
// embeddable form and open in a new tab.
const TOUR_HOSTS = [
    'matterport.com',
    'realsee.ai',
    'show.tours',
    'youriguide.com',
    'zillow.com',
];
function hostMatches(hostname, suffixes) {
    const host = hostname.replace(/^www\.|^m\./, '');
    return suffixes.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}
export function classifyTourUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        // Not even a parseable URL (a bare filename, or outright garbage like
        // the literal string "Yes" seen on one real listing) - a recognizable
        // video file extension still earns a confident "Video" label even
        // though the value is too malformed to actually link to.
        return /\.(mp4|mov|avi|webm)$/i.test(url) ? 'video' : 'unknown';
    }
    if (hostMatches(parsed.hostname, VIDEO_HOSTS))
        return 'video';
    if (hostMatches(parsed.hostname, TOUR_HOSTS))
        return 'tour';
    return 'unknown';
}
/** "Video" only when confidently a video host; a real 3D tour or
 * anything unrecognized keeps the original "Virtual Tour" label rather
 * than guessing. */
export function tourLabel(url) {
    return classifyTourUrl(url) === 'video' ? 'Video' : 'Virtual Tour';
}
/**
 * Converts a tour URL into its embeddable iframe form. Returns null for
 * anything with no known embeddable form (most tour-hosting microsites,
 * Zillow/RealSee/show.tours/YouriGuide, or an unrecognized host) -
 * callers fall back to an "open in new tab" link instead of guessing an
 * iframe will work.
 */
export function toEmbeddableTourUrl(url) {
    return toYouTubeEmbedUrl(url) ?? toVimeoEmbedUrl(url) ?? toMatterportEmbedUrl(url);
}
function toYouTubeEmbedUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return null;
    }
    const host = parsed.hostname.replace(/^www\.|^m\./, '');
    if (host === 'youtu.be') {
        const id = parsed.pathname.slice(1).split('/')[0];
        return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === 'youtube.com') {
        if (parsed.pathname === '/watch') {
            const id = parsed.searchParams.get('v');
            return id ? `https://www.youtube.com/embed/${id}` : null;
        }
        if (parsed.pathname.startsWith('/embed/')) {
            return url;
        }
    }
    return null;
}
/**
 * vimeo.com/<id> (or /<id>/<hash> for an unlisted video - the hash is
 * required as the embed's ?h= param or Vimeo 403s the iframe, confirmed
 * live against a real listing's unlisted tour) into its player.vimeo.com
 * embed form. player.vimeo.com URLs are already embeddable and pass
 * through unchanged.
 */
function toVimeoEmbedUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return null;
    }
    const host = parsed.hostname.replace(/^www\./, '');
    if (host === 'player.vimeo.com' && parsed.pathname.startsWith('/video/')) {
        return url;
    }
    if (host !== 'vimeo.com')
        return null;
    const segments = parsed.pathname.split('/').filter(Boolean);
    const id = segments[0];
    if (!id || !/^\d+$/.test(id))
        return null;
    const hash = segments[1];
    return hash ? `https://player.vimeo.com/video/${id}?h=${hash}` : `https://player.vimeo.com/video/${id}`;
}
/** Matterport share links (my.matterport.com/show/?m=<id>) already work
 * as an iframe src unmodified - no URL transform needed, just a host check. */
function toMatterportEmbedUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return null;
    }
    const host = parsed.hostname.replace(/^www\./, '');
    return host === 'matterport.com' || host.endsWith('.matterport.com') ? url : null;
}
//# sourceMappingURL=tourUrl.js.map